import axios from 'axios'
import { getMessagesBetweenUserAndAI, AI_AGENT_ID } from '../../workspace/service/workspaceMessageService'
import { getSystemPrompt } from '../../workspace/utils/AIChat/systemPrompt'
import { getSessionLanguage } from '../../workspace/service/languageConfig'

const GPT_HOST = process.env.REACT_APP_HOST + '/api/v1/gpt/openAI'

// ─── Behavioral Classification ──────────────────────────────

const BEHAVIORAL_PROMPTS = {
  COLD_START: `[BEHAVIORAL CONTEXT: This is the student's first time asking for help. They have not written or run any code yet. Give them something concrete to start with — a pseudocode scaffold with blanks to fill in. End your response with a single sentence starting with "Try:" that gives them that pseudocode starting point so they know what to write next.]`,

  PASSIVE: `[BEHAVIORAL CONTEXT: This student received help but has not written or run any code since. Try a different angle from your last response — do not repeat the same explanation. Do NOT escalate your scaffolding level — stay at the same level but approach it differently. If the student asks "how to fix" or requests the answer directly, treat this the same as the system prompt rule — acknowledge, redirect, and re-ask with pseudocode. If the student has just completed a step and is asking what to do next, start at Level 1 for the new step. If the question is purely conceptual, answer it naturally. Otherwise, end your response with a single sentence starting with "Try:" that gives them one concrete next action using pseudocode with blanks if code is involved.]`,  
  
  MODERATE: `[BEHAVIORAL CONTEXT: This student wrote some code but has not tested it. Do not give more explanation. If their current code would produce meaningful output (e.g. contains a print statement, an expression, or enough logic to show a result), end your response with a single sentence starting with "Try:" telling them to run it. If running it would produce no output, end with a "Try:" that asks them to add a specific print statement first so they can see what their code is doing.]`,

  ACTIVE: `[BEHAVIORAL CONTEXT: This student has been coding, testing, and hitting errors. They are engaged and struggling. Recognize their effort. Name the specific error and the misconception behind it so they can read errors independently. Escalate to Level 2 or 3 immediately. End your response with a single sentence starting with "Try:" giving them the targeted fix as a pseudocode pattern with blanks.]`,

  INTENSIVE: `[BEHAVIORAL CONTEXT: This student has run their code many times and keeps hitting errors. They do not need encouragement — they need to be unblocked. Name the concept or method they need, explain briefly why their current approach fails, and escalate to Level 3 immediately. End your response with a single sentence starting with "Try:" that gives them the precise pseudocode pattern they need, with blanks for the specific values.]`,
}

function classifyPreQueryBehavior(events, windowStart, queryTimestamp, hasPriorAiResponse) {
  const windowEvents = events.filter(e => e.timestamp >= windowStart && e.timestamp < queryTimestamp)
  const edits  = windowEvents.filter(e => e.type === 'CODE_TYPE' || e.type === 'CODE_DELETE').length
  const runs   = windowEvents.filter(e => e.type === 'TERMINAL_RUN').length
  const errors = windowEvents.filter(e => e.type === 'TERMINAL_ERROR').length

  // Zero activity
  if (edits === 0 && runs === 0) {
    return hasPriorAiResponse
      ? { key: 'PASSIVE', prompt: BEHAVIORAL_PROMPTS.PASSIVE }
      : { key: 'COLD_START', prompt: BEHAVIORAL_PROMPTS.COLD_START }
  }

  // Wrote code but never ran, or ran with no errors
  if (runs === 0 || (runs >= 1 && errors === 0))
    return { key: 'MODERATE', prompt: BEHAVIORAL_PROMPTS.MODERATE }

  // Extreme effort — many runs, many errors
  if (runs >= 3 && errors >= 3)
    return { key: 'INTENSIVE', prompt: BEHAVIORAL_PROMPTS.INTENSIVE }

  // Tested, hit errors (but not extreme)
  return { key: 'ACTIVE', prompt: BEHAVIORAL_PROMPTS.ACTIVE }
}

// ─── Reconstruct context ─────────────────────────────────────

function buildContextualPrompt(queryText, code, testResults, terminalOutput, taskDescription, sessionId) {
  const language = getSessionLanguage(sessionId)

  return `
${queryText}

--- CONTEXT ---
TASK INSTRUCTIONS:
${taskDescription || 'No task description provided.'}

CURRENT CODE:
\`\`\`${language}
${code || ''}
\`\`\`

TEST RESULTS:
${testResults}

TERMINAL OUTPUT:
${terminalOutput}
`
}

// ─── Reconstruct test results + terminal output from telemetry ───────────────

function getTestResultsBeforeTimestamp(events, timestamp) {
  const testEvents = events.filter(e => e.type === 'TEST_CASE_RESULT' && e.timestamp <= timestamp)
  if (testEvents.length === 0) return 'No tests run yet.'

  const last = testEvents[testEvents.length - 1]
  const passed = last.payload?.passed_count ?? 0
  const total = last.payload?.total_tests ?? 0
  return `${passed}/${total} tests passed`
}

function getTerminalOutputBeforeTimestamp(events, timestamp) {
  const terminalEvents = events.filter(e =>
    (e.type === 'TERMINAL_OUTPUT' || e.type === 'TERMINAL_ERROR') && e.timestamp <= timestamp
  )
  
  // Take last 500 chars like the original
  const combined = terminalEvents.map(e => {
    if (e.type === 'TERMINAL_ERROR') {
      return `${e.payload?.type || 'Error'}: ${e.payload?.message || ''}`
    }
    return e.payload?.output || ''
  }).join('\n')

  return combined.slice(-500)
}

// ─── Prepare OpenAI messages ─────────────────────────────────

function prepareOpenAIMessages(historyMessages, contextualPrompt, sessionId) {
  const language = getSessionLanguage(sessionId)

  return [
    { role: 'system', content: getSystemPrompt(language) },
    ...historyMessages.slice(-6).map(m => ({
      role: m.sender_id === AI_AGENT_ID ? 'assistant' : 'user',
      content: m.content,
    })),
    { role: 'user', content: contextualPrompt },
  ]
}

// ─── Process one student ─────────────────────────────────────

async function processStudent(studentId, studentName, dbMessages, telemetryEvents, taskDescription, sessionId, onQueryComplete) {
  const studentMessages = dbMessages.filter(m => m.sender_id !== AI_AGENT_ID)
  const results = []

  for (let qIdx = 0; qIdx < studentMessages.length; qIdx++) {
    const queryMsg = studentMessages[qIdx]
    const queryTimestamp = new Date(queryMsg.created_time).getTime()

    // ── 1. Get code at query time from DB ──
    const codeAtQuery = queryMsg.code_context || ''

    // ── 2. Get conversation history up to this point ──
    const historyBefore = dbMessages.filter(m =>
      new Date(m.created_time).getTime() < queryTimestamp
    )

    // ── 3. Get test results & terminal output from telemetry ──
    const testResults = getTestResultsBeforeTimestamp(telemetryEvents, queryTimestamp)
    const terminalOutput = getTerminalOutputBeforeTimestamp(telemetryEvents, queryTimestamp)

    // ── 4. Compute behavioral classification ──
    // Window starts at last AI response before this query, or session start
    const previousAiMessages = dbMessages.filter(m =>
      m.sender_id === AI_AGENT_ID && new Date(m.created_time).getTime() < queryTimestamp
    )
    const windowStart = previousAiMessages.length > 0
      ? new Date(previousAiMessages[previousAiMessages.length - 1].created_time).getTime()
      : (telemetryEvents.length > 0 ? telemetryEvents[0].timestamp : 0)

    const behavioral = classifyPreQueryBehavior(
        telemetryEvents,
        windowStart,
        queryTimestamp,
        previousAiMessages.length > 0
    )

    // ── 5. Build the counterfactual prompt (behavioral + context) ──
    const contextualPrompt = buildContextualPrompt(
      queryMsg.content,
      codeAtQuery,
      testResults,
      terminalOutput,
      taskDescription,
      sessionId,
    )

    const enrichedPrompt = behavioral.prompt + '\n\n' + contextualPrompt
    const apiPayload = prepareOpenAIMessages(historyBefore, enrichedPrompt, sessionId)

    // ── 6. Get original response from DB ──
    const originalResponse = dbMessages.find(m =>
      m.sender_id === AI_AGENT_ID && new Date(m.created_time).getTime() > queryTimestamp
    )

    // ── 7. Call GPT with counterfactual prompt ──
    let counterfactualResponse = null
    let error = null
    try {
      const gptResponse = await axios.post(GPT_HOST, { messages: apiPayload })
      counterfactualResponse = gptResponse.data.content
    } catch (err) {
      error = err.message || 'API call failed'
      console.error(`GPT call failed for ${studentName} query ${qIdx + 1}:`, err)
    }

    const result = {
      student_id: studentId,
      student_name: studentName,
      query_index: qIdx + 1,
      query_text: queryMsg.content,
      query_timestamp: queryMsg.created_time,
      code_at_query: codeAtQuery,
      code_length: codeAtQuery.length,
      test_results_at_query: testResults,
      terminal_output_at_query: terminalOutput,
      behavioral_classification: behavioral.key,
      behavioral_prompt: behavioral.prompt,
      original_response: originalResponse?.content || null,
      counterfactual_response: counterfactualResponse,
      error,
      window_start: new Date(windowStart).toISOString(),
      history_length: historyBefore.length,
    }

    results.push(result)

    if (onQueryComplete) {
      onQueryComplete({
        studentId,
        studentName,
        queryIndex: qIdx + 1,
        totalQueries: studentMessages.length,
        classification: behavioral.key,
      })
    }
  }

  return results
}

// ─── Main batch runner ───────────────────────────────────────

export async function runCounterfactual(students, eventsByUser, sessionId, taskDescription, onProgress) {
  const allResults = []
  let totalProcessed = 0
  let totalQueries = 0

  // Count total queries first for progress
  const studentQueue = []
  for (const student of students) {
    const events = eventsByUser[student.id] || eventsByUser[String(student.id)] || []
    const hasQueries = events.some(e => e.type === 'CHAT_QUERY')
    if (!hasQueries) continue
    studentQueue.push(student)
  }

  if (onProgress) onProgress({ phase: 'loading', message: `Loading DB messages for ${studentQueue.length} students...`, done: 0, total: studentQueue.length })

  for (let sIdx = 0; sIdx < studentQueue.length; sIdx++) {
    const student = studentQueue[sIdx]
    const name = student.display_name || student.username || `${student.first_name || ''} ${student.last_name || ''}`.trim() || `Student ${student.id}`
    const events = eventsByUser[student.id] || eventsByUser[String(student.id)] || []

    // Fetch DB messages for this student
    let dbMessages = []
    try {
      dbMessages = await getMessagesBetweenUserAndAI(student.id, sessionId)
    } catch (err) {
      console.error(`Failed to fetch messages for ${name}:`, err)
      continue
    }

    const studentQueryCount = dbMessages.filter(m => m.sender_id !== AI_AGENT_ID).length
    if (studentQueryCount === 0) continue

    totalQueries += studentQueryCount

    if (onProgress) onProgress({
      phase: 'running',
      message: `Processing ${name} (${sIdx + 1}/${studentQueue.length})`,
      done: totalProcessed,
      total: totalQueries,
      currentStudent: name,
    })

    const results = await processStudent(
      student.id,
      name,
      dbMessages,
      events,
      taskDescription,
      sessionId,
      (queryProgress) => {
        totalProcessed++
        if (onProgress) onProgress({
          phase: 'running',
          message: `${queryProgress.studentName}: query ${queryProgress.queryIndex}/${queryProgress.totalQueries} [${queryProgress.classification}]`,
          done: totalProcessed,
          total: totalQueries,
          currentStudent: queryProgress.studentName,
        })
      }
    )

    allResults.push(...results)
  }

  if (onProgress) onProgress({
    phase: 'complete',
    message: `Done! ${allResults.length} queries processed across ${studentQueue.length} students.`,
    done: allResults.length,
    total: allResults.length,
  })

  return allResults
}

// ─── Export as JSON ──────────────────────────────────────────

export function downloadCounterfactualResults(results, sessionId) {
  const summary = {
    session_id: sessionId,
    generated_at: new Date().toISOString(),
    total_queries: results.length,
    total_students: [...new Set(results.map(r => r.student_id))].length,
    classification_distribution: results.reduce((acc, r) => {
      acc[r.behavioral_classification] = (acc[r.behavioral_classification] || 0) + 1
      return acc
    }, {}),
    errors: results.filter(r => r.error).length,
  }

  const output = {
    summary,
    results,
  }

  const blob = new Blob([JSON.stringify(output, null, 2)], { type: 'application/json;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `session_${sessionId}_counterfactual_${new Date().toISOString().slice(0, 10)}.json`
  a.click()
  URL.revokeObjectURL(url)
}